import PropTypes from "prop-types";
import elementType from "prop-types-extra/lib/elementType";

import { Component as ReactComponent } from "react";
import createChainedFunction from "./utils/createChainedFunction";

const propTypes = {
  href: PropTypes.string,
  onClick: PropTypes.func,
  onKeyDown: PropTypes.func,
  disabled: PropTypes.bool,
  role: PropTypes.string,
  tabIndex: PropTypes.oneOfType([
    PropTypes.number, PropTypes.string,
  ]),
  /**
   * this is sort of silly but needed for Button
   */
  componentClass: elementType,
};

const defaultProps = {
  componentClass: "a",
};

function isTrivialHref(href: any) {
  return !href || href.trim() === "#";
}

/**
 * There are situations due to browser quirks or Bootstrap CSS where
 * an anchor tag is needed, when semantically a button tag is the
 * better choice. SafeAnchor ensures that when an anchor is used like a
 * button its accessible. It also emulates input `disabled` behavior for
 * links, which is usually desirable for Buttons, NavItems, MenuItems, etc.
 */
class SafeAnchor extends ReactComponent {
  constructor(props: any, context: any) {
    super(props, context);

    this.handleClick = this.handleClick.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
  }

  handleClick(event: any) {
    const { disabled, href, onClick } = this.props;

    if (disabled || isTrivialHref(href)) {
      event.preventDefault();
    }

    if (disabled) {
      event.stopPropagation();
      return;
    }

    if (onClick) {
      onClick(event);
    }
  }

  handleKeyDown(event: any) {
    if (event.key === " ") {
      event.preventDefault();
      this.handleClick(event);
    }
  }

  render() {
    const {
      componentClass: Component, disabled, onKeyDown, ...props
    } = this.props;

    if (isTrivialHref(props.href)) {
      props.role = props.role || "button";
      // we want to make sure there is a href attribute on the node
      // otherwise, the cursor incorrectly styled (except with role='button')
      props.href = props.href || "#";
    }

    if (disabled) {
      props.tabIndex = -1;
      props.style = { pointerEvents: "none", ...props.style };
    }

    return (
      <Component
        {...props}
        onClick={this.handleClick}
        onKeyDown={createChainedFunction(this.handleKeyDown, onKeyDown)}
      />
    );
  }
}

SafeAnchor.propTypes = propTypes;
SafeAnchor.defaultProps = defaultProps;

export default SafeAnchor;
